import { TypedDocumentNode } from '@apollo/client';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client/react';
import {
  AcceptedLegalDocuments,
  Mutation,
  MutationAcceptLegalDocumentsArgs,
  Query
} from 'graphql/generated/graphqlTypes';
import { ACCEPT_LEGAL_DOCUMENTS } from 'graphql/mutations';
import { GET_TENANT_LEGAL_DOCUMENTS } from 'graphql/queries';
import { useEffect } from 'react';
import { setCurrentTenantAction } from 'state/actions/currentTenant';
import { setTenantLegalDocumentsAction } from 'state/actions/tenantLegalDocuments';
import { tenantLegalDocumentsVar } from 'state/vars';
import { ignoreHandledMutationError } from 'utils/Mutation/ignoreHandledMutationError';
import { BaseHookProps } from '../shared/types';

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const GET_TENANT_LEGAL_DOCUMENTS_TYPED: TypedDocumentNode<Pick<Query, 'getTenantLegalDocuments'>> =
  GET_TENANT_LEGAL_DOCUMENTS;
const ACCEPT_LEGAL_DOCUMENTS_TYPED: TypedDocumentNode<
  Pick<Mutation, 'acceptLegalDocuments'>,
  MutationAcceptLegalDocumentsArgs
> = ACCEPT_LEGAL_DOCUMENTS;

const useTenantLegalDocuments = ({ handleFetchError }: BaseHookProps) => {
  const tenantLegalDocuments = useReactiveVar(tenantLegalDocumentsVar);

  const [getTenantLegalDocuments, { loading }] = useLazyQuery(GET_TENANT_LEGAL_DOCUMENTS_TYPED);

  useEffect(() => {
    const fetchTenantLegalDocuments = async () => {
      try {
        const { data } = await getTenantLegalDocuments();
        if (data) {
          setTenantLegalDocumentsAction(data.getTenantLegalDocuments);
        }
      } catch {
        handleFetchError('Error while fetching legal documents');
      }
    };
    void fetchTenantLegalDocuments();
  }, [getTenantLegalDocuments]);

  const [acceptedLegalDocumentsMutation, { loading: acceptedLegalDocumentsLoading }] = useMutation(
    ACCEPT_LEGAL_DOCUMENTS_TYPED,
    {
      onCompleted: (data) => {
        if (data.acceptLegalDocuments.__typename === 'BasicTenant') setCurrentTenantAction(data.acceptLegalDocuments);
        else if (data.acceptLegalDocuments.__typename === 'AgreementRejected')
          handleFetchError(`Error while accepting Legal Documents: ${data.acceptLegalDocuments.reason}`);
      },
      onError: () => {
        handleFetchError('Error while accepting Legal Documents');
      }
    }
  );

  const acceptedLegalDocuments = async (acceptedLegalDocumentsVariables: AcceptedLegalDocuments) => {
    await ignoreHandledMutationError(
      acceptedLegalDocumentsMutation({
        variables: {
          acceptedLegalDocuments: acceptedLegalDocumentsVariables
        }
      })
    );
  };

  return {
    tenantLegalDocuments,
    loading,
    acceptedLegalDocuments,
    acceptedLegalDocumentsLoading
  };
};

export default useTenantLegalDocuments;
